'use client'

import * as React from 'react'
import { X, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'

const FOCUSABLE_SELECTOR =
    'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'

/**
 * DetailFlyout - A right-side panel for viewing/editing record details
 * 
 * Best Practices for Flyout Interactions:
 * 
 * 1. CONTEXT PRESERVATION: The flyout maintains the user's position in the
 *    data table, unlike modals which obscure the underlying content.
 * 
 * 2. PROGRESSIVE DISCLOSURE: Show summary in table row, detailed view in flyout.
 *    User clicks a row → flyout reveals full details without losing their place.
 * 
 * 3. KEYBOARD ACCESSIBILITY: 
 *    - Escape key closes the flyout
 *    - Focus is trapped within the flyout when open
 *    - Focus returns to trigger element when closed
 * 
 * 4. WIDTH RECOMMENDATIONS:
 *    - sm (360px): Quick previews, single-column content, mobile-friendly
 *    - md (420px): Standard details view (DEFAULT) - balances content with table visibility
 *    - lg (520px): Complex forms, multi-section layouts, rich content
 * 
 * 5. WHEN TO USE vs DIALOGS:
 *    - USE FLYOUT: Viewing details, editing in context, activities/notes
 *    - USE DIALOG: Confirmations, focused forms (add new), destructive actions
 */

interface DetailFlyoutProps {
    open: boolean
    onClose: () => void
    title: string
    subtitle?: string
    children: React.ReactNode
    /** Optional header actions (e.g., Edit, Delete buttons) */
    headerActions?: React.ReactNode
    /** Optional sticky footer content */
    footer?: React.ReactNode
    /** Panel width: sm=360px, md=420px (default), lg=520px, xl=600px */
    width?: 'sm' | 'md' | 'lg' | 'xl'
    /** Optional breadcrumb-style back button text */
    backLabel?: string
    /**
     * When true, the flyout ignores its own Escape/overlay-click close. Set it
     * while a Sheet or Dialog is stacked on top so Escape closes only the
     * topmost layer (the flyout is a plain document listener, not a Radix
     * dialog, so it can't rely on Radix's own layering to defer to it).
     */
    disableEscape?: boolean
}

// Widths are maximums: on viewports narrower than the size the panel clamps
// to the screen instead of overflowing past the left edge.
const widthClasses = {
    sm: 'w-screen max-w-90',
    md: 'w-screen max-w-105',
    lg: 'w-screen max-w-130',
    xl: 'w-screen max-w-150',
}

export function DetailFlyout({
    open,
    onClose,
    title,
    subtitle,
    children,
    headerActions,
    footer,
    width = 'md',
    backLabel,
    disableEscape = false,
}: DetailFlyoutProps) {
    const panelRef = React.useRef<HTMLDivElement>(null)
    const closeButtonRef = React.useRef<HTMLButtonElement>(null)

    // Escape key + body scroll lock. A stacked Sheet sets disableEscape so its
    // own Escape handler wins; the defaultPrevented check is a second guard for
    // any layer that marks the event first.
    React.useEffect(() => {
        if (!open) return

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !disableEscape && !e.defaultPrevented) onClose()
        }
        // Focus trap: the panel advertises aria-modal, so Tab must cycle inside
        // it instead of walking into the dimmed page behind. While a Sheet or
        // Dialog is stacked on top (disableEscape) that layer owns focus.
        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== 'Tab' || disableEscape || e.defaultPrevented) return
            const panel = panelRef.current
            if (!panel) return
            const focusable = Array.from(
                panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
            ).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true' && el.tabIndex !== -1)
            if (focusable.length === 0) {
                e.preventDefault()
                return
            }
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            const active = document.activeElement as HTMLElement | null
            const inside = active ? panel.contains(active) : false
            if (e.shiftKey) {
                if (!inside || active === first) {
                    e.preventDefault()
                    last.focus()
                }
            } else if (!inside || active === last) {
                e.preventDefault()
                first.focus()
            }
        }
        document.addEventListener('keydown', handleEscape)
        document.addEventListener('keydown', handleTab)
        document.body.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('keydown', handleEscape)
            document.removeEventListener('keydown', handleTab)
            document.body.style.overflow = ''
        }
    }, [open, onClose, disableEscape])

    // Restore focus to whatever opened the flyout (e.g. the table row's name
    // button) when it closes, so a keyboard user keeps their place. Captured on
    // open; restored in cleanup.
    const openerRef = React.useRef<HTMLElement | null>(null)
    React.useEffect(() => {
        if (!open) return
        openerRef.current = document.activeElement as HTMLElement | null
        return () => openerRef.current?.focus?.()
    }, [open])

    // Initial focus on open — depends only on `open` so parent re-renders
    // don't yank focus out of fields the user is typing in.
    React.useEffect(() => {
        if (!open) return
        const t = setTimeout(() => closeButtonRef.current?.focus(), 100)
        return () => clearTimeout(t)
    }, [open])

    if (!open) return null

    return (
        <>
            {/* Backdrop overlay */}
            <div
                className="fixed inset-0 z-40 bg-[var(--bg-inverse)]/20 backdrop-blur-[2px] animate-overlay-in"
                onClick={disableEscape ? undefined : onClose}
                aria-hidden="true"
            />

            {/* Flyout Panel. While a Sheet/Dialog is stacked on top
                (disableEscape), the panel shares its z-50 with the Sheet and a
                sliver can peek past a narrower Sheet's overlay — so make the
                whole panel inert to clicks, matching the Escape/overlay gating.
                The stacked Sheet is portaled to <body>, not a child here, so it
                stays interactive. */}
            <aside
                ref={panelRef}
                className={cn(
                    // h-dvh, not h-screen: on iPad/iPhone Safari 100vh includes the
                    // area behind the browser toolbar, which pushes the footer (and
                    // its Save button) off-screen. Matches Sheet.
                    'fixed right-0 top-0 z-50 h-dvh bg-[var(--bg-surface)] flex flex-col animate-slide-in-right',
                    widthClasses[width],
                    disableEscape && 'pointer-events-none'
                )}
                style={{
                    boxShadow: 'var(--shadow-flyout)',
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="flyout-title"
            >
                {/* Header - sticky */}
                <header className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[var(--border-subtle)] shrink-0 bg-[var(--bg-surface)]">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Close button */}
                        <Button
                            ref={closeButtonRef}
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            aria-label="Close panel"
                            className="shrink-0 -ml-2"
                        >
                            <X className="h-5 w-5" />
                        </Button>

                        {/* Title block */}
                        <div className="min-w-0 flex-1">
                            {backLabel && (
                                <button
                                    onClick={onClose}
                                    className="flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-0.5 transition-colors"
                                >
                                    <ChevronRight className="h-3 w-3 rotate-180" />
                                    {backLabel}
                                </button>
                            )}
                            <h2
                                id="flyout-title"
                                className="text-lg font-semibold text-[var(--text-primary)] truncate"
                            >
                                {title}
                            </h2>
                            {subtitle && (
                                <p className="text-sm text-[var(--text-secondary)] truncate mt-0.5">{subtitle}</p>
                            )}
                        </div>
                    </div>

                    {/* Header actions */}
                    {headerActions && (
                        <div className="flex items-center gap-2 shrink-0">
                            {headerActions}
                        </div>
                    )}
                </header>

                {/* Content - scrollable */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {children}
                </div>

                {/* Footer - sticky (optional) */}
                {footer && (
                    <footer className="px-6 py-4 border-t border-[var(--border-subtle)] shrink-0 bg-[var(--bg-subtle)]">
                        {footer}
                    </footer>
                )}
            </aside>
        </>
    )
}

/**
 * Flyout Section - Organizes content within a flyout into labeled sections
 */
interface FlyoutSectionProps {
    title: string
    description?: string
    children: React.ReactNode
    /** Optional action button for the section header */
    action?: React.ReactNode
    className?: string
}

export function FlyoutSection({
    title,
    description,
    children,
    action,
    className
}: FlyoutSectionProps) {
    return (
        <section className={cn("space-y-3", className)}>
            <div className="flex items-center justify-between gap-2">
                <div>
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
                    {description && (
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">{description}</p>
                    )}
                </div>
                {action}
            </div>
            {children}
        </section>
    )
}

/**
 * Flyout Field - Displays a label/value pair in the flyout
 */
interface FlyoutFieldProps {
    label: string
    value: React.ReactNode
    /** If true, the value is displayed in muted style */
    muted?: boolean
}

export function FlyoutField({ label, value, muted }: FlyoutFieldProps) {
    return (
        <div className="flex flex-col gap-0.5">
            <dt className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">
                {label}
            </dt>
            <dd className={cn(
                "text-sm",
                muted ? "text-[var(--text-tertiary)]" : "text-[var(--text-primary)]"
            )}>
                {value || <span className="text-[var(--text-disabled)]">—</span>}
            </dd>
        </div>
    )
}

/**
 * Flyout Divider - Visual separator between sections
 */
export function FlyoutDivider() {
    return <hr className="border-[var(--border-subtle)] my-5" />
}
